import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, CheckCircle2, Circle, Clock, AlertCircle, Trash2,
  Edit3, X, Filter, Calendar, Tag, Building2, Scale, ChevronDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "audience",  label: "Audience",          color: "bg-red-100 text-red-800 border-red-300" },
  { id: "deadline",  label: "Délai légal",        color: "bg-orange-100 text-orange-800 border-orange-300" },
  { id: "client",    label: "Client",             color: "bg-blue-100 text-blue-800 border-blue-300" },
  { id: "drafting",  label: "Rédaction",          color: "bg-violet-100 text-violet-800 border-violet-300" },
  { id: "research",  label: "Recherche",          color: "bg-teal-100 text-teal-800 border-teal-300" },
  { id: "admin",     label: "Admin",              color: "bg-slate-100 text-slate-800 border-slate-300" },
  { id: "general",   label: "Général",            color: "bg-gray-100 text-gray-800 border-gray-300" },
];

const PRIORITIES = [
  { id: "urgent", label: "Urgente", color: "text-red-700", dot: "bg-red-500" },
  { id: "high",   label: "Haute",   color: "text-orange-700", dot: "bg-orange-500" },
  { id: "normal", label: "Normale", color: "text-blue-700", dot: "bg-blue-500" },
  { id: "low",    label: "Faible",  color: "text-slate-600", dot: "bg-slate-400" },
];

const STATUSES = [
  { id: "todo",        label: "À faire",   icon: Circle,       color: "text-slate-500" },
  { id: "in_progress", label: "En cours",  icon: Clock,        color: "text-blue-600" },
  { id: "done",        label: "Terminé",   icon: CheckCircle2, color: "text-green-600" },
];

function getCategoryConfig(id) { return CATEGORIES.find(c => c.id === id) || CATEGORIES[6]; }
function getPriorityConfig(id) { return PRIORITIES.find(p => p.id === id) || PRIORITIES[2]; }
function getStatusConfig(id)   { return STATUSES.find(s => s.id === id) || STATUSES[0]; }

function isOverdue(dueDate, status) {
  if (!dueDate || status === "done") return false;
  return new Date(dueDate) < new Date(new Date().toISOString().split('T')[0]);
}

export default function Todos({ user, cabinet, onLogout }) {
  const [todos, setTodos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");

  const [form, setForm] = useState({
    title: "", description: "", category: "general", priority: "normal",
    due_date: "", client_name: "", legal_deadline: false, tags: []
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const [todosRes, statsRes] = await Promise.all([
        axios.get(`${API}/todos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/todos/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTodos(todosRes.data.todos || []);
      setStats(statsRes.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    try {
      const token = localStorage.getItem('lawliet_token');
      if (editingTodo) {
        await axios.put(`${API}/todos/${editingTodo.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Tâche mise à jour");
      } else {
        await axios.post(`${API}/todos`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Tâche créée");
      }
      setShowForm(false);
      setEditingTodo(null);
      resetForm();
      loadData();
    } catch { toast.error("Erreur"); }
  };

  const updateStatus = async (todo, newStatus) => {
    try {
      const token = localStorage.getItem('lawliet_token');
      await axios.put(`${API}/todos/${todo.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch { toast.error("Erreur"); }
  };

  const deleteTodo = async (id) => {
    try {
      const token = localStorage.getItem('lawliet_token');
      await axios.delete(`${API}/todos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tâche supprimée");
      loadData();
    } catch { toast.error("Erreur"); }
  };

  const resetForm = () => {
    setForm({ title: "", description: "", category: "general", priority: "normal",
      due_date: "", client_name: "", legal_deadline: false, tags: [] });
  };

  const openEdit = (todo) => {
    setEditingTodo(todo);
    setForm({
      title: todo.title, description: todo.description || "",
      category: todo.category, priority: todo.priority,
      due_date: todo.due_date ? todo.due_date.split('T')[0] : "",
      client_name: todo.client_name || "", legal_deadline: todo.legal_deadline || false,
      tags: todo.tags || []
    });
    setShowForm(true);
  };

  const filteredTodos = todos.filter(t => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterCategory !== "all" && t.category !== filterCategory) return false;
    return true;
  });

  const groupedTodos = {
    urgent: filteredTodos.filter(t => t.priority === "urgent" && t.status !== "done"),
    other: filteredTodos.filter(t => t.priority !== "urgent" && t.status !== "done"),
    done: filteredTodos.filter(t => t.status === "done"),
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="todos-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-blue-900" style={{ fontFamily: 'EB Garamond, serif' }}>
              Mes Tâches
            </h1>
            <p className="text-slate-600 mt-1">Gérez vos échéances, audiences et délais de procédure</p>
          </div>
          <Button onClick={() => { resetForm(); setEditingTodo(null); setShowForm(true); }} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="add-todo-btn">
            <Plus className="h-4 w-4" /> Nouvelle tâche
          </Button>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: "Total", value: stats.total, color: "text-slate-900" },
              { label: "À faire", value: stats.todo, color: "text-slate-600" },
              { label: "En cours", value: stats.in_progress, color: "text-blue-700" },
              { label: "Terminées", value: stats.done, color: "text-green-700" },
              { label: "En retard", value: stats.overdue, color: stats.overdue > 0 ? "text-red-700 font-bold" : "text-slate-400" },
            ].map(s => (
              <Card key={s.label}>
                <CardContent className="pt-4 pb-3 text-center">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-slate-600 font-medium">Filtres :</span>
          {["all", "todo", "in_progress", "done"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filterStatus === s ? "bg-blue-600 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
              }`}>
              {s === "all" ? "Tous" : getStatusConfig(s).label}
            </button>
          ))}
          <span className="text-slate-300">|</span>
          {["all", ...CATEGORIES.map(c => c.id)].slice(0, 6).map(c => (
            <button key={c} onClick={() => setFilterCategory(c)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filterCategory === c ? "bg-blue-600 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
              }`}>
              {c === "all" ? "Toutes catégories" : getCategoryConfig(c).label}
            </button>
          ))}
        </div>

        {/* Todo Lists */}
        {loading ? (
          <div className="text-center py-16 text-slate-400">Chargement...</div>
        ) : filteredTodos.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-400 opacity-60" />
              <p className="text-slate-600 font-medium">Aucune tâche ici</p>
              <p className="text-sm text-slate-500 mt-1">Créez votre première tâche avec le bouton ci-dessus</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Urgent */}
            {groupedTodos.urgent.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-bold text-red-700 uppercase tracking-wide">Urgentes</span>
                </div>
                <div className="space-y-2">
                  {groupedTodos.urgent.map(todo => (
                    <TodoCard key={todo.id} todo={todo} onStatusChange={updateStatus} onEdit={openEdit} onDelete={deleteTodo} />
                  ))}
                </div>
              </div>
            )}
            {/* Other */}
            {groupedTodos.other.length > 0 && (
              <div>
                {groupedTodos.urgent.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <Circle className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Autres tâches</span>
                  </div>
                )}
                <div className="space-y-2">
                  {groupedTodos.other.map(todo => (
                    <TodoCard key={todo.id} todo={todo} onStatusChange={updateStatus} onEdit={openEdit} onDelete={deleteTodo} />
                  ))}
                </div>
              </div>
            )}
            {/* Done */}
            {groupedTodos.done.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-700 uppercase tracking-wide">Terminées ({groupedTodos.done.length})</span>
                </div>
                <div className="space-y-2 opacity-70">
                  {groupedTodos.done.map(todo => (
                    <TodoCard key={todo.id} todo={todo} onStatusChange={updateStatus} onEdit={openEdit} onDelete={deleteTodo} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => { setShowForm(false); setEditingTodo(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 text-lg">
                {editingTodo ? "Modifier la tâche" : "Nouvelle tâche"}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingTodo(null); }} className="p-1.5 hover:bg-slate-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <Label>Titre *</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Ex: Rédiger conclusions d'appel" className="mt-1" />
              </div>
              <div>
                <Label>Description</Label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
                  rows={2} placeholder="Détails supplémentaires..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Catégorie</Label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                    {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Priorité</Label>
                  <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                    {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Échéance</Label>
                <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <Label>Client (optionnel)</Label>
                <Input value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} placeholder="Nom du client ou dossier" className="mt-1" />
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <input type="checkbox" id="legal-deadline" checked={form.legal_deadline}
                  onChange={e => setForm({...form, legal_deadline: e.target.checked})}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                <label htmlFor="legal-deadline" className="text-sm text-amber-900 cursor-pointer">
                  Délai réglementaire / procédural (forclusion, prescription...)
                </label>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingTodo(null); }}>Annuler</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
                {editingTodo ? "Mettre à jour" : "Créer la tâche"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function TodoCard({ todo, onStatusChange, onEdit, onDelete }) {
  const catConf = getCategoryConfig(todo.category);
  const priConf = getPriorityConfig(todo.priority);
  const statConf = getStatusConfig(todo.status);
  const StatusIcon = statConf.icon;
  const overdue = isOverdue(todo.due_date, todo.status);

  const nextStatus = { todo: "in_progress", in_progress: "done", done: "todo" };

  return (
    <div className={`bg-white border-2 rounded-xl p-4 flex items-start gap-3 transition hover:shadow-sm ${
      todo.status === "done" ? "border-slate-200 opacity-70" :
      overdue ? "border-red-300 bg-red-50" :
      todo.priority === "urgent" ? "border-orange-300" :
      "border-slate-200 hover:border-blue-300"
    }`} data-testid={`todo-item-${todo.id}`}>
      {/* Status toggle */}
      <button onClick={() => onStatusChange(todo, nextStatus[todo.status])} className="mt-0.5 flex-shrink-0">
        <StatusIcon className={`h-5 w-5 ${statConf.color} hover:scale-110 transition`} />
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-semibold ${todo.status === "done" ? "line-through text-slate-400" : "text-slate-900"}`}>
            {todo.title}
          </p>
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => onEdit(todo)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
              <Edit3 className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => onDelete(todo.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {todo.description && (
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{todo.description}</p>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <Badge className={`text-xs border ${catConf.color}`}>{catConf.label}</Badge>
          <span className="flex items-center gap-1 text-xs text-slate-600">
            <span className={`h-2 w-2 rounded-full ${priConf.dot}`} />
            {priConf.label}
          </span>
          {todo.due_date && (
            <span className={`flex items-center gap-1 text-xs ${overdue ? "text-red-700 font-semibold" : "text-slate-500"}`}>
              <Calendar className="h-3 w-3" />
              {new Date(todo.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
              {overdue && " — En retard"}
            </span>
          )}
          {todo.client_name && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Building2 className="h-3 w-3" /> {todo.client_name}
            </span>
          )}
          {todo.legal_deadline && (
            <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-300">
              <Scale className="h-3 w-3 mr-1" /> Délai légal
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}
